import React, { useState, useEffect } from "react";
import axiosInstance from '../services/axiosInstance';
import GlobalTable from "../components/GlobalTable";
import { useLazyTable } from "../hooks/useLazyTable";
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { FilterMatchMode } from 'primereact/api';
import { usePermission } from '../contexts/UserContext';

const SupplierAdmin = () => {
    const [supplier, setSupplier] = useState({
        name: "",
        contact_phone: "",
        email: "",
        credit_limit: 0.00,
        credit_duration: 0,
        organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
    });
    const [suppliers, setSuppliers] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const canManageSuppliers = usePermission('manage_suppliers');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const supplierResult = await axiosInstance.get("/api/suppliers/", { params: queryParams });
                setSuppliers(supplierResult.data.results);
                setTotalRecords(supplierResult.data.count);
                setLoading(false);
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
        };
        fetchData();
    }, [queryParams]);

    const handleChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setSupplier((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const result = await axiosInstance.post("/api/suppliers/", supplier);
            setSuppliers([...suppliers, result.data]);
            setSupplier({
                name: "",
                contact_phone: "",
                email: "",
                credit_limit: 0.00,
                credit_duration: 0,
                organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
            });
            handleApiSuccess(`Supplier created: ${result.data.name}`);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleRowEditComplete = async (e) => {
        const { newData, index } = e;
        try {
            const updatedSupplier = await axiosInstance.put(`/api/suppliers/${newData.id}/`, newData);
            const updatedSuppliers = [...suppliers];
            updatedSuppliers[index] = updatedSupplier.data;
            setSuppliers(updatedSuppliers);
            handleApiSuccess("Supplier updated successfully!");
        } catch (error) {
            handleApiError(error);
        }
    };

    const columns = React.useMemo(
        () => [
            { Header: "Name", accessor: "name", sortable: true, filtered:true, },
            { Header: "Contact Phone", accessor: "contact_phone", sortable: true, filtered:true, },
            { Header: "Email", accessor: "email", sortable: true, filtered:true, },
            { Header: "Credit Duration", accessor: "credit_duration", sortable: true, filtered:true, dataType: "numeric", matchMode: FilterMatchMode.EQUALS },
            { Header: "Credit Limit", accessor: "credit_limit", sortable: true, filtered:true, dataType: "numeric", matchMode: FilterMatchMode.EQUALS },
        ],
        []
    );

    const headerAction = canManageSuppliers ? (
        <Button type="button" icon="pi pi-plus" label=" Add Supplier" outlined onClick={() => setDialogVisible(true)} className="w-full sm:w-auto" />
    ) : null;
         
    return (
        <div>
            <div className="containers">
                <GlobalTable
                    columns={columns}
                    data={suppliers}
                    tabletitle="Supplier List"
                    onRowEditComplete={handleRowEditComplete}
                    showEditColumn={canManageSuppliers}
                    headerAction={headerAction}
                    loading={loading}
                    lazy
                    totalRecords={totalRecords}
                    lazyParams={lazyParams}
                    onPage={onPage}
                    onSort={onSort}
                    onFilter={onFilter}
                />
            </div>
            <Dialog
                header="Add Supplier"
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                modal
                style={{ width: '32rem', borderRadius: '12px', padding: '.5rem'}} 
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                className="p-fluid"
            >
            <form id="dialogForm" onSubmit={handleSubmit}>
            <div style={{ borderRadius: '12px', padding: '1rem' }}>
                <div className="field" >
                    <label htmlFor="supplier_name" className="font-bold">Supplier Name:</label>
                    <InputText
                        id="supplier_name"
                        value={supplier.name}
                        onChange={(e) => handleChange(e, "name")}
                        placeholder="Enter Supplier Name"
                        required
                    />                           
                </div>
                <div className="field">
                    <label htmlFor="contact_phone" className="font-bold">Contact Information:</label>
                    <InputText
                        id="contact_phone"
                        value={supplier.contact_phone}
                        onChange={(e) => handleChange(e, "contact_phone")}
                        placeholder="Enter Phone Number"
                        maxLength={10}
                    />                           
                </div>
                <div className="field">
                    <label htmlFor="email" className="font-bold">Email:</label>
                    <InputText
                        id="email"
                        value={supplier.email}
                        onChange={(e) => handleChange(e, "email")}
                        placeholder="Enter Email"
                    />
                </div>
                <div className="field">
                    <label htmlFor="credit_limit" className="font-bold">Supplier Credit limit:</label>
                    <InputNumber
                        id="credit_limit"
                        value={supplier.credit_limit}
                        onValueChange={(e) => handleChange(e, "credit_limit")}
                        min={0} mode="currency" currency="LKR"
                        placeholder="Enter Supplier Credit limit"
                    />                           
                </div>
                <div className="field">
                    <label htmlFor="credit_duration" className="font-bold">Credit Duration (Days):</label>
                    <InputNumber
                        id="credit_duration"
                        value={supplier.credit_duration}
                        onValueChange={(e) => handleChange(e, "credit_duration")}
                        placeholder="Enter Credit Duration"
                    />                           
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <Button label="Save" icon="pi pi-check" form="dialogForm" type='submit' style={{borderRadius:"6px", maxWidth: "120px"}} />
                <Button label="Cancel" icon="pi pi-times" outlined onClick={(e) => {setDialogVisible(false); e.preventDefault();}}  style={{borderRadius:"6px", maxWidth: "120px"}} />
            </div> 
            </form>
            </Dialog>
        </div>
    );
};

export default SupplierAdmin;